import React, { useState, useMemo, useEffect } from "react";
import { useTheme } from "styled-components";
import api from "../../../services/api";
import {
  FiArrowLeft,
  FiPackage,
  FiTag,
  FiDollarSign,
  FiTrendingUp,
  FiPercent,
  FiFileText,
  FiLayers,
  FiBox,
  FiCalendar,
  FiActivity,
  FiBarChart2,
  FiShoppingCart,
  FiTruck,
  FiHash,
  FiDownload,
  FiUsers,
  FiRefreshCw,
} from "react-icons/fi";
import { GoOrganization, GoGraph, GoLaw } from "react-icons/go";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { Container } from "./styles";
import ProductDetailSkeleton from "../skeletons/ProductDetailSkeleton";

// Função para formatar valores monetários
const formatNumber = (value) => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatDate = (date) => {
  if (!date) return "Não informado";
  return new Intl.DateTimeFormat("pt-BR").format(new Date(date));
};

const MONTH_NAMES = {
  "01": "Jan", "02": "Fev", "03": "Mar", "04": "Abr", "05": "Mai", "06": "Jun",
  "07": "Jul", "08": "Ago", "09": "Set", "10": "Out", "11": "Nov", "12": "Dez"
};

// ==================== CRONOGRAMA REFORMA TRIBUTÁRIA ====================
const CRONOGRAMA_REFORMA = {
  2026: { cbs: 0.009, ibs: 0.001, reducaoAntigos: 0.00, label: "Início" },
  2027: { cbs: 0.088, ibs: 0.001, reducaoAntigos: 0.00, label: "CBS Plena" },
  2028: { cbs: 0.088, ibs: 0.001, reducaoAntigos: 0.00, label: "Transição" },
  2029: { cbs: 0.088, ibs: 0.023, reducaoAntigos: 0.10, label: "IBS 10%" },
  2030: { cbs: 0.088, ibs: 0.046, reducaoAntigos: 0.20, label: "IBS 20%" },
  2031: { cbs: 0.088, ibs: 0.069, reducaoAntigos: 0.30, label: "IBS 30%" },
  2032: { cbs: 0.088, ibs: 0.092, reducaoAntigos: 0.40, label: "IBS 40%" },
  2033: { cbs: 0.088, ibs: 0.177, reducaoAntigos: 1.00, label: "Pleno" },
};

// ==================== COMPONENTE REFORMA TAB ====================
function ReformaTab({ produto, theme, formatCurrency, formatNumber }) {
  const [selectedCenario, setSelectedCenario] = useState("2027");

  // Valores base do produto
  const valorBase = produto.faturamentoTotal || produto.valorVendas || 0;
  const icmsAtual = produto.icmsTotal || produto.icms || 0;
  const pisAtual = produto.pisTotal || produto.pis || 0;
  const cofinsAtual = produto.cofinsTotal || produto.cofins || 0;
  const ipiAtual = produto.ipiTotal || produto.ipi || 0;
  const totalAtual = icmsAtual + pisAtual + cofinsAtual + ipiAtual;

  // Alíquotas do cenário selecionado
  const cenario = CRONOGRAMA_REFORMA[selectedCenario] || CRONOGRAMA_REFORMA["2027"];
  const aliquotaCBS = cenario.cbs;
  const aliquotaIBS = cenario.ibs;

  // Cálculos IVA Dual
  const cbsProjetado = valorBase * aliquotaCBS;
  const ibsProjetado = valorBase * aliquotaIBS;
  const totalIvaDual = cbsProjetado + ibsProjetado;

  // Impacto
  const impacto = totalIvaDual - totalAtual;
  const variacaoPercentual = totalAtual > 0 ? ((impacto / totalAtual) * 100) : 0;

  const reformaKpis = {
    debitosAtuais: totalAtual,
    ivaDual: totalIvaDual,
    cbs: cbsProjetado,
    ibs: ibsProjetado,
    aliquotaCBS: aliquotaCBS * 100,
    aliquotaIBS: aliquotaIBS * 100,
    icms: icmsAtual,
    pis: pisAtual,
    cofins: cofinsAtual,
    ipi: ipiAtual,
    impacto: impacto,
    variacaoPercentual: variacaoPercentual
  };

  return (
    <>
      {/* Timeline da Reforma */}
      <div className="reform-timeline">
        <button 
          className={`timeline-item ${selectedCenario === "2027" ? "active" : ""}`}
          onClick={() => setSelectedCenario("2027")}
        >
          <span className="timeline-year">2027</span>
          <span className="timeline-label">CBS Plena</span>
        </button>
        <button 
          className={`timeline-item ${selectedCenario === "2028" ? "active" : ""}`}
          onClick={() => setSelectedCenario("2028")}
        >
          <span className="timeline-year">2028</span>
          <span className="timeline-label">Transição</span>
        </button>
        <button 
          className={`timeline-item ${selectedCenario === "2029" ? "active" : ""}`}
          onClick={() => setSelectedCenario("2029")}
        >
          <span className="timeline-year">2029</span>
          <span className="timeline-label">IBS 10%</span>
        </button>
        <button 
          className={`timeline-item ${selectedCenario === "2030" ? "active" : ""}`}
          onClick={() => setSelectedCenario("2030")}
        >
          <span className="timeline-year">2030</span>
          <span className="timeline-label">IBS 20%</span>
        </button>
        <button 
          className={`timeline-item ${selectedCenario === "2031" ? "active" : ""}`}
          onClick={() => setSelectedCenario("2031")}
        >
          <span className="timeline-year">2031</span>
          <span className="timeline-label">IBS 30%</span>
        </button>
        <button 
          className={`timeline-item ${selectedCenario === "2032" ? "active" : ""}`}
          onClick={() => setSelectedCenario("2032")}
        >
          <span className="timeline-year">2032</span>
          <span className="timeline-label">IBS 40%</span>
        </button>
        <button 
          className={`timeline-item ${selectedCenario === "2033" ? "active" : ""}`}
          onClick={() => setSelectedCenario("2033")}
        >
          <span className="timeline-year">2033</span>
          <span className="timeline-label">IVA Pleno</span>
        </button>
      </div>

      {/* Linha 1: Situação Atual vs Reforma */}
      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card primary">
          <div className="card-content">
            <div className="thumb">
              <FiDollarSign />
            </div>
            <div className="text">
              <p>{formatNumber(reformaKpis.debitosAtuais)}</p>
              <p>Débitos Atuais ({selectedCenario})</p>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb">
              <FiPercent />
            </div>
            <div className="text">
              <p>{((reformaKpis.debitosAtuais / valorBase) * 100).toFixed(1)}%</p>
              <p>Alíquota Efetiva Atual</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb">
              <GoGraph />
            </div>
            <div className="text">
              <p>{formatNumber(reformaKpis.ivaDual)}</p>
              <p>IVA Dual ({selectedCenario})</p>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb">
              <FiPercent />
            </div>
            <div className="text">
              <p>{((reformaKpis.ivaDual / valorBase) * 100).toFixed(1)}%</p>
              <p>Alíquota Efetiva Projetada</p>
            </div>
          </div>
        </div>
      </div>

      {/* Linha 2: Detalhamento CBS e IBS */}
      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card tax-cbs">
          <div className="card-content">
            <div className="thumb">
              <GoLaw />
            </div>
            <div className="text">
              <p>{formatNumber(reformaKpis.cbs)}</p>
              <p>CBS - Contribuição sobre Bens e Serviços</p>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb">
              <FiPercent />
            </div>
            <div className="text">
              <p>{reformaKpis.aliquotaCBS.toFixed(2)}%</p>
              <p>Alíquota CBS ({selectedCenario})</p>
            </div>
          </div>
        </div>
        <div className="kpi-card tax-ibs">
          <div className="card-content">
            <div className="thumb">
              <GoLaw />
            </div>
            <div className="text">
              <p>{formatNumber(reformaKpis.ibs)}</p>
              <p>IBS - Imposto sobre Bens e Serviços</p>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb">
              <FiPercent />
            </div>
            <div className="text">
              <p>{reformaKpis.aliquotaIBS.toFixed(2)}%</p>
              <p>Alíquota IBS ({selectedCenario})</p>
            </div>
          </div>
        </div>
      </div>

      {/* Linha 3: Análise de Impacto */}
      <div className="kpi-grid kpi-grid-2">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb">
              <FiDollarSign />
            </div>
            <div className="text">
              <p>{formatNumber(reformaKpis.impacto)}</p>
              <p>Diferença em Valores (R$)</p>
            </div>
          </div>
        </div>
        <div className={`kpi-card ${reformaKpis.impacto >= 0 ? 'warning' : 'success'}`}>
          <div className="card-content">
            <div className="thumb">
              <FiTrendingUp />
            </div>
            <div className="text">
              <p>{reformaKpis.impacto >= 0 ? '+' : ''}{reformaKpis.variacaoPercentual.toFixed(1)}%</p>
              <p>Variação da Carga Tributária</p>
            </div>
          </div>
        </div>
      </div>

      {/* Linha 4: Comparativo Impostos Atuais vs Reforma - Donut Charts */}
      <div className="section-row">
        {/* Donut Chart - Sistema Atual */}
        <div className="stats-card chart-card">
          <div className="card-header">
            <div className="header-title">
              <GoGraph />
              <h4>Sistema Atual - Distribuição de Impostos ({selectedCenario})</h4>
            </div>
          </div>
          <div className="donut-wrapper" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", padding: "30px 24px", gap: "32px", minHeight: "280px" }}>
            <div className="donut-chart" style={{ flexShrink: 0 }}>
              {(() => {
                const taxes = [
                  { name: "ICMS", value: reformaKpis.icms, color: "#f59e0b" },
                  { name: "PIS", value: reformaKpis.pis, color: "#ef4444" },
                  { name: "COFINS", value: reformaKpis.cofins, color: "#10b981" },
                  { name: "IPI", value: reformaKpis.ipi, color: "#8b5cf6" },
                ];
                const activeTaxes = taxes.filter((t) => t.value > 0);
                const total = taxes.reduce((sum, t) => sum + t.value, 0);

                let currentAngle = -90;
                const radius = 80;
                const innerRadius = 50;
                const cx = 100, cy = 100;

                return (
                  <>
                    <svg width="180" height="180" viewBox="0 0 200 200">
                      {activeTaxes.map((tax, index) => {
                        const percentage = tax.value / total;
                        const angle = percentage * 360;
                        const startAngle = currentAngle;
                        
                        // Special case: if angle is 360 (100%), draw full donut ring using two semicircles
                        if (angle >= 359.9) {
                          currentAngle += angle;
                          const path1 = `M ${cx} ${cy - radius} A ${radius} ${radius} 0 0 1 ${cx} ${cy + radius} A ${radius} ${radius} 0 0 1 ${cx} ${cy - radius} Z M ${cx} ${cy - innerRadius} A ${innerRadius} ${innerRadius} 0 0 0 ${cx} ${cy + innerRadius} A ${innerRadius} ${innerRadius} 0 0 0 ${cx} ${cy - innerRadius} Z`;
                          return (
                            <g key={index}>
                              <path d={path1} fill={tax.color} fillRule="evenodd" />
                              <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#fff"
                                fontSize="11"
                                fontWeight="600"
                              >
                                100%
                              </text>
                            </g>
                          );
                        }

                        const startRad = (startAngle * Math.PI) / 180;
                        const endRad = ((startAngle + angle) * Math.PI) / 180;
                        const largeArc = angle > 180 ? 1 : 0;

                        const x1 = cx + radius * Math.cos(startRad);
                        const y1 = cy + radius * Math.sin(startRad);
                        const x2 = cx + radius * Math.cos(endRad);
                        const y2 = cy + radius * Math.sin(endRad);
                        const ix1 = cx + innerRadius * Math.cos(startRad);
                        const iy1 = cy + innerRadius * Math.sin(startRad);
                        const ix2 = cx + innerRadius * Math.cos(endRad);
                        const iy2 = cy + innerRadius * Math.sin(endRad);

                        const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                        const midAngle = startAngle + angle / 2;
                        const midRad = (midAngle * Math.PI) / 180;
                        const labelR = (radius + innerRadius) / 2;
                        const labelX = cx + labelR * Math.cos(midRad);
                        const labelY = cy + labelR * Math.sin(midRad);

                        currentAngle += angle;

                        return (
                          <g key={index}>
                            <path d={path} fill={tax.color} />
                            {percentage > 0.08 && (
                              <text
                                x={labelX}
                                y={labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#fff"
                                fontSize="11"
                                fontWeight="600"
                              >
                                {(percentage * 100).toFixed(0)}%
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                    <div className="donut-center">
                      <span className="donut-total">{formatNumber(total)}</span>
                      <span className="donut-label">Total</span>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="categories-list" style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
              {[
                { name: "ICMS", value: reformaKpis.icms, color: "#f59e0b" },
                { name: "PIS", value: reformaKpis.pis, color: "#ef4444" },
                { name: "COFINS", value: reformaKpis.cofins, color: "#10b981" },
                { name: "IPI", value: reformaKpis.ipi, color: "#8b5cf6" },
              ].map((tax, index) => (
                <div key={index} className="category-item" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                  <div className="category-header" style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                    <div
                      className="category-color"
                      style={{ backgroundColor: tax.color, width: "12px", height: "12px", borderRadius: "3px", flexShrink: 0 }}
                    ></div>
                    <span className="category-name" style={{ flex: 1, fontSize: "0.85em", fontWeight: 500 }}>{tax.name}</span>
                    <span className="category-count" style={{ fontSize: "0.8em", fontWeight: 600, padding: "4px 10px", borderRadius: "6px", background: "rgba(255,255,255,0.05)" }}>{formatNumber(tax.value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Donut Chart - IVA Dual */}
        <div className="stats-card chart-card">
          <div className="card-header">
            <div className="header-title">
              <GoLaw />
              <h4>Reforma Tributária - IVA Dual ({selectedCenario})</h4>
            </div>
          </div>
          <div className="donut-wrapper" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", padding: "30px 24px", gap: "32px", minHeight: "280px" }}>
            <div className="donut-chart" style={{ flexShrink: 0 }}>
              {(() => {
                const taxes = [
                  { name: "CBS", value: reformaKpis.cbs, color: "#f59e0b" },
                  { name: "IBS", value: reformaKpis.ibs, color: "#3b82f6" },
                ];
                const activeTaxes = taxes.filter((t) => t.value > 0);
                const total = taxes.reduce((sum, t) => sum + t.value, 0);

                let currentAngle = -90;
                const radius = 80;
                const innerRadius = 50;
                const cx = 100, cy = 100;

                return (
                  <>
                    <svg width="180" height="180" viewBox="0 0 200 200">
                      {activeTaxes.map((tax, index) => {
                        const percentage = tax.value / total;
                        const angle = percentage * 360;
                        const startAngle = currentAngle;
                        const startRad = (startAngle * Math.PI) / 180;
                        const endRad = ((startAngle + angle) * Math.PI) / 180;
                        const largeArc = angle > 180 ? 1 : 0;

                        const x1 = cx + radius * Math.cos(startRad);
                        const y1 = cy + radius * Math.sin(startRad);
                        const x2 = cx + radius * Math.cos(endRad);
                        const y2 = cy + radius * Math.sin(endRad);
                        const ix1 = cx + innerRadius * Math.cos(startRad);
                        const iy1 = cy + innerRadius * Math.sin(startRad);
                        const ix2 = cx + innerRadius * Math.cos(endRad);
                        const iy2 = cy + innerRadius * Math.sin(endRad);

                        const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                        const midAngle = startAngle + angle / 2;
                        const midRad = (midAngle * Math.PI) / 180;
                        const labelR = (radius + innerRadius) / 2;
                        const labelX = cx + labelR * Math.cos(midRad);
                        const labelY = cy + labelR * Math.sin(midRad);

                        currentAngle += angle;

                        return (
                          <g key={index}>
                            <path d={path} fill={tax.color} />
                            {percentage > 0.08 && (
                              <text
                                x={labelX}
                                y={labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#fff"
                                fontSize="11"
                                fontWeight="600"
                              >
                                {(percentage * 100).toFixed(0)}%
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                    <div className="donut-center">
                      <span className="donut-total">{formatNumber(total)}</span>
                      <span className="donut-label">Total</span>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="categories-list" style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
              {[
                { name: "CBS", value: reformaKpis.cbs, color: "#f59e0b" },
                { name: "IBS", value: reformaKpis.ibs, color: "#3b82f6" },
              ].map((tax, index) => (
                <div key={index} className="category-item" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                  <div className="category-header" style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                    <div
                      className="category-color"
                      style={{ backgroundColor: tax.color, width: "12px", height: "12px", borderRadius: "3px", flexShrink: 0 }}
                    ></div>
                    <span className="category-name" style={{ flex: 1, fontSize: "0.85em", fontWeight: 500 }}>{tax.name}</span>
                    <span className="category-count" style={{ fontSize: "0.8em", fontWeight: 600, padding: "4px 10px", borderRadius: "6px", background: "rgba(255,255,255,0.05)" }}>{formatNumber(tax.value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

export default function ProductDetail({ produto, onVoltar, initialTab = "geral" }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Sales History State
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesPage, setSalesPage] = useState(1);
  const [salesHasMore, setSalesHasMore] = useState(true);
  const [salesLoading, setSalesLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');
  const [salesObserverTarget, setSalesObserverTarget] = useState(null);

  // Purchase History State
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchaseHasMore, setPurchaseHasMore] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseSortOrder, setPurchaseSortOrder] = useState('desc');
  const [purchaseObserverTarget, setPurchaseObserverTarget] = useState(null);

  // Download XML function
  const handleDownloadXml = async (chave, e) => {
    e.stopPropagation();
    if (!chave) {
      alert('Chave não disponível para download');
      return;
    }
    try {
      const response = await api.get(`/movimentacoes/download/${chave}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${chave}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar XML:", error);
      alert("Erro ao baixar XML. Tente novamente.");
    }
  };

  useEffect(() => {
    async function fetchDetail() {
      try {
        setLoading(true);
        const projectId = localStorage.getItem("vidal_selected_project");
        if (!projectId) throw new Error("Projeto não selecionado");
        
        // Codigo do item deve ser encodeURIComponent para evitar problemas com caracteres especiais
        const codigo = encodeURIComponent(produto.codigo);
        const response = await api.get(`/products/${projectId}/detail/${codigo}`);
        
        if (response.data.success) {
          setDetailData(response.data.data);
        } else {
          setError("Falha ao carregar detalhes do produto.");
        }
      } catch (err) {
        console.error("Erro ao buscar detalhes:", err);
        setError("Erro ao carregar dados do produto.");
      } finally {
        setLoading(false);
      }
    }
    
    if (produto?.codigo) {
      fetchDetail();
    }
  }, [produto]);

  // Fetch Sales History
  const fetchSalesHistory = async (page = 1, resetList = false) => {
    if (salesLoading || (!salesHasMore && !resetList)) return;
    
    try {
      setSalesLoading(true);
      const projectId = localStorage.getItem("vidal_selected_project");
      if (!projectId) return;
      
      const codigo = encodeURIComponent(produto.codigo);
      const response = await api.get(
        `/products/${projectId}/detail/${codigo}/sales-history`,
        { params: { page, limit: 20, sortOrder } }
      );
      
      if (response.data.success) {
        const newSales = response.data.data.vendas;
        setSalesHistory(prev => resetList ? newSales : [...prev, ...newSales]);
        setSalesHasMore(response.data.data.pagination.hasMore);
        setSalesPage(page);
      }
    } catch (err) {
      console.error("Erro ao buscar histórico de vendas:", err);
    } finally {
      setSalesLoading(false);
    }
  };

  // Initial load and sort order change
  useEffect(() => {
    if (produto?.codigo && activeTab === "movimentacoes") {
      fetchSalesHistory(1, true);
    }
  }, [produto, sortOrder, activeTab]);

  // Infinite scroll observer for sales
  useEffect(() => {
    if (!salesObserverTarget || salesLoading || !salesHasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && salesHasMore && !salesLoading) {
          fetchSalesHistory(salesPage + 1, false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(salesObserverTarget);
    return () => observer.disconnect();
  }, [salesObserverTarget, salesHasMore, salesLoading, salesPage]);

  // Fetch Purchase History
  const fetchPurchaseHistory = async (page = 1, resetList = false) => {
    if (purchaseLoading || (!purchaseHasMore && !resetList)) return;
    
    try {
      setPurchaseLoading(true);
      const projectId = localStorage.getItem("vidal_selected_project");
      if (!projectId) return;
      
      const codigo = encodeURIComponent(produto.codigo);
      const response = await api.get(
        `/products/${projectId}/detail/${codigo}/purchase-history`,
        { params: { page, limit: 20, sortOrder: purchaseSortOrder } }
      );
      
      if (response.data.success) {
        const newPurchases = response.data.data.compras;
        setPurchaseHistory(prev => resetList ? newPurchases : [...prev, ...newPurchases]);
        setPurchaseHasMore(response.data.data.pagination.hasMore);
        setPurchasePage(page);
      }
    } catch (err) {
      console.error("Erro ao buscar histórico de compras:", err);
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Initial load and sort order change for purchases
  useEffect(() => {
    if (produto?.codigo && activeTab === "movimentacoes") {
      fetchPurchaseHistory(1, true);
    }
  }, [produto, purchaseSortOrder, activeTab]);

  // Infinite scroll observer for purchases
  useEffect(() => {
    if (!purchaseObserverTarget || purchaseLoading || !purchaseHasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && purchaseHasMore && !purchaseLoading) {
          fetchPurchaseHistory(purchasePage + 1, false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(purchaseObserverTarget);
    return () => observer.disconnect();
  }, [purchaseObserverTarget, purchaseHasMore, purchaseLoading, purchasePage]);

  // Merge e Mapeamento dos dados
  const produtoData = useMemo(() => {
    if (!detailData) return { ...produto }; // Fallback inicial

    const d = detailData;
    const p = d.produto || {};
    
    return {
      ...produto, // Dados da lista (fallback)
      ...p,
      
      // Sobrescrever com dados frescos
      codigo: p.codigo,
      descricao: p.descricao,
      unidade: p.unidade,
      ncm: p.ncm,
      tipo: p.tipo,
      categoria: p.ncm ? p.ncm.substring(0, 2) : "00",
      
      // Financeiro
      faturamentoTotal: d.faturamento || 0,
      custoTotal: d.totalCompras || 0,
      valorTotalCompras: d.totalCompras || 0,
      valorTotalVendas: d.receita || 0,
      valorMedioVenda: d.operacionais?.ticketMedioVenda || 0,
      valorMedioCompra: d.operacionais?.ticketMedioCompra || 0,
      
      // Quantidades
      quantidadeVendida: d.estoque?.saidas || 0,
      quantidadeComprada: d.estoque?.entradas || 0,
      quantidadeEstoque: d.estoque?.atual || 0,
      
      // Impostos Totais
      icmsTotal: d.impostos?.icms || 0,
      pisTotal: d.impostos?.pis || 0,
      cofinsTotal: d.impostos?.cofins || 0,
      ipiTotal: d.impostos?.ipi || 0,
      
      // Alíquotas (Estimadas ou do cadastro)
      aliquotaIcms: p.aliqIcms || 0,
      aliquotaPis: 1.65, // Default se não vier
      aliquotaCofins: 7.6, // Default se não vier
      
      // Informações Fiscais (Mocks se não vier do backend ainda)
      cstIcms: "000",
      cstIcmsDescricao: "Tributada integralmente",
      cfop: "5102",
      cfopDescricao: "Venda de mercadoria",
      cstPis: "01",
      cstPisDescricao: "Operação Tributável",
      cstCofins: "01",
      cstCofinsDescricao: "Operação Tributável",
      
      // Contagens
      clientesUnicos: d.operacionais?.clientesUnicos || 0,
      fornecedoresUnicos: d.operacionais?.fornecedoresUnicos || 0,
      notasVenda: d.operacionais?.nfVendas || 0,
      notasCompra: d.operacionais?.nfCompras || 0,
    };
  }, [detailData, produto]);

  // Evolução Gráfica
  const evolucaoMovimentacao = useMemo(() => {
    if (!detailData?.evolucao) return [];
    
    return detailData.evolucao.map(item => {
      const mesNum = item.mes.split("-")[1]; // yyyy-MM
      return {
        ...item,
        mes: MONTH_NAMES[mesNum] || mesNum,
        // Garantir números
        vendas: item.vendas || 0,
        compras: item.compras || 0,
        icms: item.icms || 0,
        pisCofins: item.pisCofins || 0
      };
    });
  }, [detailData]);

  // Listas Top
  const principaisClientes = useMemo(() => detailData?.principaisClientes || [], [detailData]);
  const principaisFornecedores = useMemo(() => detailData?.principaisFornecedores || [], [detailData]);

  // Cálculos derivados
  const totalImpostos = produtoData.icmsTotal + produtoData.pisTotal + produtoData.cofinsTotal + (produtoData.ipiTotal || 0);
  const cargaTributaria = produtoData.faturamentoTotal > 0 
    ? ((totalImpostos / produtoData.faturamentoTotal) * 100).toFixed(1) 
    : "0.0";

  // Renderização
  if (loading) {
     return (
        <Container theme={theme}>
            {activeTab === "geral" ? (
              <ProductDetailSkeleton />
            ) : (
              <div className="content-wrapper">
                <button className="back-button" onClick={onVoltar}>
                  <FiArrowLeft />
                  <span>Voltar</span>
                </button>
                <div className="product-header">
                  <div className="product-info-bar">
                    <div className="product-icon">
                      <FiPackage />
                    </div>
                    <div className="product-content">
                      <h2>Carregando...</h2>
                    </div>
                  </div>
                </div>
                <div className="tabs-container">
                  <button className="tab" onClick={() => setActiveTab("geral")}>
                    <FiLayers />
                    <span>Geral</span>
                  </button>
                  <button className="tab" onClick={() => setActiveTab("movimentacoes")}>
                    <FiRefreshCw />
                    <span>Movimentações</span>
                  </button>
                  <button className="tab active">
                    <GoLaw />
                    <span>Reforma Tributária</span>
                  </button>
                </div>
                <ProductDetailSkeleton />
              </div>
            )}
        </Container>
     );
  }

  if (error) {
     return (
        <Container theme={theme}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.text }}>
                <FiFileText size={40} style={{ marginBottom: 20, color: '#ef4444' }} />
                <h3>{error}</h3>
                <button className="back-button" onClick={onVoltar} style={{ marginTop: 20 }}>
                  <FiArrowLeft /> Voltar
                </button>
            </div>
        </Container>
     );
  }

  return (
    <Container theme={theme}>
      <div className="content-wrapper">
      {/* Botão Voltar */}
      <button className="back-button" onClick={onVoltar}>
        <FiArrowLeft />
        <span>Voltar</span>
      </button>

      {/* Header com botão voltar */}
      <div className="product-header">
        <div className="product-info-bar">
          <div className="product-icon">
            <FiPackage />
          </div>
          <div className="product-content">
            <span className="product-eyebrow">PRODUTO · {produtoData.codigo}</span>
            <h2>{produtoData.descricao}</h2>
            <div className="product-badges">
              <span className="badge">
                <strong>NCM:</strong> {produtoData.ncm}
              </span>
              <span className="badge">
                <strong>Tipo:</strong> {produtoData.tipo}
              </span>
              <span className="badge">
                <strong>Unidade:</strong> {produtoData.unidade}
              </span>
              <span className="badge">
                <strong>Estoque:</strong> {produtoData.quantidadeEstoque?.toLocaleString("pt-BR")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs de Navegação */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === "geral" ? "active" : ""}`}
          onClick={() => setActiveTab("geral")}
        >
          <FiLayers />
          <span>Geral</span>
        </button>
        <button
          className={`tab ${activeTab === "movimentacoes" ? "active" : ""}`}
          onClick={() => setActiveTab("movimentacoes")}
        >
          <FiRefreshCw />
          <span>Movimentações</span>
        </button>
        <button
          className={`tab ${activeTab === "reforma" ? "active" : ""}`}
          onClick={() => setActiveTab("reforma")}
        >
          <GoLaw />
          <span>Reforma Tributária</span>
        </button>
      </div>

      {/* Conteúdo da Aba Geral */}
      {activeTab === "geral" && (
        <>
          {/* Linha 1: Valores Financeiros */}
      <div className="kpi-grid kpi-grid-3">
        <div className="kpi-card primary">
          <div className="card-content">
            <div className="thumb">
              <FiDollarSign />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.faturamentoTotal)}</p>
              <p>Faturamento</p>
            </div>
          </div>
        </div>
        <div className="kpi-card info">
          <div className="card-content">
            <div className="thumb">
              <FiTruck />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.valorTotalCompras || 0)}</p>
              <p>Valor em Compras</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb">
              <FiShoppingCart />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.valorTotalVendas || produtoData.faturamentoTotal)}</p>
              <p>Valor em Vendas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Linha 2: Quantidades e Tickets Médios */}
      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb">
              <FiBox />
            </div>
            <div className="text">
              <p>{produtoData.quantidadeComprada.toLocaleString("pt-BR")}</p>
              <p>Quantidade de Compra</p>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb">
              <FiPackage />
            </div>
            <div className="text">
              <p>{produtoData.quantidadeVendida.toLocaleString("pt-BR")}</p>
              <p>Quantidade de Venda</p>
            </div>
          </div>
        </div>
        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb">
              <FiTruck />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.valorMedioCompra)}</p>
              <p>Ticket Médio em Compras</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb">
              <FiTrendingUp />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.valorMedioVenda)}</p>
              <p>Ticket Médio em Vendas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Linha 3: Impostos */}
      <div className="kpi-grid kpi-grid-5">
        <div className="kpi-card tax-icms">
          <div className="card-content">
            <div className="thumb">
              <FiFileText />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.icmsTotal)}</p>
              <p>ICMS</p>
            </div>
          </div>
        </div>
        <div className="kpi-card tax-piscofins">
          <div className="card-content">
            <div className="thumb">
              <FiFileText />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.pisTotal)}</p>
              <p>PIS</p>
            </div>
          </div>
        </div>
        <div className="kpi-card tax-piscofins">
          <div className="card-content">
            <div className="thumb">
              <FiFileText />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.cofinsTotal)}</p>
              <p>COFINS</p>
            </div>
          </div>
        </div>
        <div className="kpi-card tax-ipi">
          <div className="card-content">
            <div className="thumb">
              <FiFileText />
            </div>
            <div className="text">
              <p>{formatNumber(produtoData.ipiTotal || 0)}</p>
              <p>IPI</p>
            </div>
          </div>
        </div>
        <div className="kpi-card tax-total">
          <div className="card-content">
            <div className="thumb">
              <GoLaw />
            </div>
            <div className="text">
              <p>{formatNumber(totalImpostos)}</p>
              <p>Total de Impostos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas Operacionais — KPIs */}
      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card primary">
          <div className="card-content">
            <div className="thumb"><FiTrendingUp /></div>
            <div className="text">
              <p>{produtoData.notasVenda}</p>
              <p>Notas Fiscais (Venda)</p>
            </div>
          </div>
        </div>
        <div className="kpi-card info">
          <div className="card-content">
            <div className="thumb"><FiTruck /></div>
            <div className="text">
              <p>{produtoData.notasCompra}</p>
              <p>Notas Fiscais (Compra)</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><FiUsers /></div>
            <div className="text">
              <p>{produtoData.clientesUnicos}</p>
              <p>Clientes Únicos</p>
            </div>
          </div>
        </div>
        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb"><GoOrganization /></div>
            <div className="text">
              <p>{produtoData.fornecedoresUnicos}</p>
              <p>Fornecedores Únicos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Parâmetros Fiscais — KPIs */}
      <div className="kpi-grid kpi-grid-3">
        <div className="kpi-card tax-icms">
          <div className="card-content">
            <div className="thumb"><FiFileText /></div>
            <div className="text">
              <p>{produtoData.cstIcms}</p>
              <p>CST ICMS</p>
            </div>
          </div>
        </div>
        <div className="kpi-card tax-cbs">
          <div className="card-content">
            <div className="thumb"><FiTag /></div>
            <div className="text">
              <p>{produtoData.cfop}</p>
              <p>CFOP Padrão</p>
            </div>
          </div>
        </div>
        <div className="kpi-card tax-ibs">
          <div className="card-content">
            <div className="thumb"><FiPercent /></div>
            <div className="text">
              <p>{produtoData.aliquotaIcms}%</p>
              <p>Alíquota ICMS Cad</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="section-row">
        {/* Evolução de Movimentação */}
        <div className="stats-card chart-card">
          <div className="card-header">
            <div className="header-title">
              <FiTrendingUp />
              <h4>Evolução de Movimentação</h4>
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: "#3b82f6" }}
                ></div>
                <span>Vendas</span>
              </div>
              <div className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: "#10b981" }}
                ></div>
                <span>Compras</span>
              </div>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={evolucaoMovimentacao}
                margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="gradVendasLine" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradComprasLine" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.border}
                  vertical={false}
                />
                <XAxis
                  dataKey="mes"
                  tick={{ fill: theme.text, fontSize: 10, opacity: 0.7 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: theme.text, fontSize: 9, opacity: 0.6 }}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <Tooltip
                  cursor={false}
                  contentStyle={{
                    background: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: "8px",
                  }}
                  formatter={(value) => [formatCurrency(value), ""]}
                />
                <Line
                  type="monotone"
                  dataKey="vendas"
                  name="Vendas"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: "#3b82f6", r: 3 }}
                  activeDot={{ r: 5 }}
                  fill="url(#gradVendasLine)"
                />
                <Line
                  type="monotone"
                  dataKey="compras"
                  name="Compras"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: "#10b981", r: 3 }}
                  activeDot={{ r: 5 }}
                  fill="url(#gradComprasLine)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Impostos Mensais */}
        <div className="stats-card chart-card">
          <div className="card-header">
            <div className="header-title">
              <FiBarChart2 />
              <h4>Impostos Mensais</h4>
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: "#3b82f6" }}
                ></div>
                <span>ICMS</span>
              </div>
              <div className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: "#10b981" }}
                ></div>
                <span>PIS/COFINS</span>
              </div>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={evolucaoMovimentacao}
                margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="gradIcms" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradPisCofins" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.border}
                  vertical={false}
                />
                <XAxis
                  dataKey="mes"
                  tick={{ fill: theme.text, fontSize: 10, opacity: 0.7 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: theme.text, fontSize: 9, opacity: 0.6 }}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <Tooltip
                  cursor={false}
                  contentStyle={{
                    background: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: "8px",
                  }}
                  formatter={(value) => [formatCurrency(value), ""]}
                />
                <Bar
                  dataKey="icms"
                  name="ICMS"
                  fill="url(#gradIcms)"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="pisCofins"
                  name="PIS/COFINS"
                  fill="url(#gradPisCofins)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Principais Clientes */}
      <div className="stats-card">
        <div className="card-header">
          <div className="header-title">
            <GoOrganization />
            <h4>Principais Clientes do Produto</h4>
          </div>
          <div className="header-badge">
            {principaisClientes.length} Clientes
          </div>
        </div>
        <div className="products-table">
          <div className="table-header">
            <div className="col-cliente">Cliente</div>
            <div className="col-quantidade">Quantidade</div>
            <div className="col-valor">Valor Total</div>
            <div className="col-participacao">Participação</div>
            <div className="col-notas">Notas</div>
          </div>
          {principaisClientes.length === 0 && (
             <div className="table-row" style={{justifyContent: 'center', padding: 20, color: theme.textSecondary}}>
                Nenhum cliente encontrado para este período.
             </div>
          )}
          {principaisClientes.map((cliente) => (
            <div key={cliente.id} className="table-row">
              <div className="col-cliente">
                <div className="product-info">
                  <div
                    className="product-color"
                    style={{ backgroundColor: cliente.cor }}
                  ></div>
                  <div className="product-details">
                    <span className="product-name">{cliente.nome}</span>
                    <span className="product-code">{cliente.cnpj}</span>
                  </div>
                </div>
              </div>
              <div className="col-quantidade">
                <span className="quantity-badge">
                  {cliente.quantidade.toLocaleString("pt-BR")} {produtoData.unidade}
                </span>
              </div>
              <div className="col-valor">
                <span className="value-text">{formatCurrency(cliente.valor)}</span>
              </div>
              <div className="col-participacao">
                <div className="participation-bar">
                  <div
                    className="participation-fill"
                    style={{
                      width: `${cliente.participacao}%`,
                      backgroundColor: cliente.cor,
                    }}
                  ></div>
                  <span className="participation-text">
                    {cliente.participacao.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="col-notas">
                <span className="notes-badge">{cliente.notas}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Principais Fornecedores */}
      <div className="stats-card">
        <div className="card-header">
          <div className="header-title">
            <FiTruck />
            <h4>Principais Fornecedores do Produto</h4>
          </div>
          <div className="header-badge">
            {principaisFornecedores.length} Fornecedores
          </div>
        </div>
        <div className="products-table">
          <div className="table-header">
            <div className="col-cliente">Fornecedor</div>
            <div className="col-quantidade">Quantidade</div>
            <div className="col-valor">Valor Total</div>
            <div className="col-participacao">Participação</div>
            <div className="col-notas">Notas</div>
          </div>
          {principaisFornecedores.length === 0 && (
             <div className="table-row" style={{justifyContent: 'center', padding: 20, color: theme.textSecondary}}>
                Nenhum fornecedor encontrado para este período.
             </div>
          )}
          {principaisFornecedores.map((fornecedor) => (
            <div key={fornecedor.id} className="table-row">
              <div className="col-cliente">
                <div className="product-info">
                  <div
                    className="product-color"
                    style={{ backgroundColor: fornecedor.cor }}
                  ></div>
                  <div className="product-details">
                    <span className="product-name">{fornecedor.nome}</span>
                    <span className="product-code">{fornecedor.cnpj}</span>
                  </div>
                </div>
              </div>
              <div className="col-quantidade">
                <span className="quantity-badge">
                  {fornecedor.quantidade.toLocaleString("pt-BR")} {produtoData.unidade}
                </span>
              </div>
              <div className="col-valor">
                <span className="value-text">{formatCurrency(fornecedor.valor)}</span>
              </div>
              <div className="col-participacao">
                <div className="participation-bar">
                  <div
                    className="participation-fill"
                    style={{
                      width: `${fornecedor.participacao}%`,
                      backgroundColor: fornecedor.cor,
                    }}
                  ></div>
                  <span className="participation-text">
                    {fornecedor.participacao.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="col-notas">
                <span className="notes-badge">{fornecedor.notas}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      </>
      )}

      {/* Conteúdo da Aba Movimentações */}
      {activeTab === "movimentacoes" && (
        <div className="history-grid">
          {/* Histórico de Vendas */}
          <div className="history-table-wrapper">
            <div className="history-header-bar">
              <div className="header-title-group">
                <div className="header-icon"><FiShoppingCart /></div>
                <h4>Histórico de Vendas</h4>
              </div>
              <div className="header-meta">
                <button
                  className="sort-button"
                  onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                  title={sortOrder === 'desc' ? 'Mais recentes primeiro' : 'Mais antigas primeiro'}
                >
                  <FiCalendar />
                  {sortOrder === 'desc' ? 'Recentes' : 'Antigas'}
                </button>
                <span className="history-count">{salesHistory.length} registros</span>
              </div>
            </div>
            <div className="history-table">
              <div className="table-header">
                <div>Cliente</div>
                <div>Qtd</div>
                <div>Valor Un.</div>
                <div>Total</div>
                <div></div>
              </div>
              {salesHistory.length === 0 && !salesLoading && (
                <div className="history-empty">
                  Nenhuma venda encontrada para este produto.
                </div>
              )}
              {salesHistory.map((venda) => (
                <div
                  key={`${venda.chaveNfe}-${venda.numeroNota}`}
                  className="table-row"
                >
                  <div>
                    <div className="product-info">
                      <div className="product-color" style={{ backgroundColor: '#3b82f6' }}></div>
                      <div className="product-details">
                        <span className="product-name">{venda.cliente}</span>
                        <span className="product-code">{new Date(venda.data).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <span className="quantity-badge">{venda.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} {produtoData.unidade}</span>
                  </div>

                  <div>
                    <span className="value-text">{formatCurrency(venda.valorUnitario)}</span>
                  </div>

                  <div>
                    <span className="value-highlight">{formatCurrency(venda.valorTotal)}</span>
                  </div>

                  <div>
                    <button
                      className="download-btn"
                      onClick={(e) => handleDownloadXml(venda.chaveNfe, e)}
                      title="Baixar XML"
                    >
                      <FiDownload />
                    </button>
                  </div>
                </div>
              ))}
              {salesLoading && (
                <div className="history-loader">
                  <div className="spinner" />
                  Carregando...
                </div>
              )}
              {salesHasMore && !salesLoading && salesHistory.length > 0 && (
                <div ref={setSalesObserverTarget} style={{ height: '10px' }} />
              )}
            </div>
          </div>

          {/* Histórico de Compras */}
          <div className="history-table-wrapper">
            <div className="history-header-bar">
              <div className="header-title-group">
                <div className="header-icon"><FiTruck /></div>
                <h4>Histórico de Compras</h4>
              </div>
              <div className="header-meta">
                <button
                  className="sort-button"
                  onClick={() => setPurchaseSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                  title={purchaseSortOrder === 'desc' ? 'Mais recentes primeiro' : 'Mais antigas primeiro'}
                >
                  <FiCalendar />
                  {purchaseSortOrder === 'desc' ? 'Recentes' : 'Antigas'}
                </button>
                <span className="history-count">{purchaseHistory.length} registros</span>
              </div>
            </div>
            <div className="history-table">
              <div className="table-header">
                <div>Fornecedor</div>
                <div>Qtd</div>
                <div>Valor Un.</div>
                <div>Total</div>
                <div></div>
              </div>
              {purchaseHistory.length === 0 && !purchaseLoading && (
                <div className="history-empty">
                  Nenhuma compra encontrada para este produto.
                </div>
              )}
              {purchaseHistory.map((compra) => (
                <div
                  key={`${compra.chaveNfe}-${compra.numeroNota}`}
                  className="table-row"
                >
                  <div>
                    <div className="product-info">
                      <div className="product-color" style={{ backgroundColor: '#10b981' }}></div>
                      <div className="product-details">
                        <span className="product-name">{compra.fornecedor}</span>
                        <span className="product-code">{new Date(compra.data).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <span className="quantity-badge">{compra.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} {produtoData.unidade}</span>
                  </div>

                  <div>
                    <span className="value-text">{formatCurrency(compra.valorUnitario)}</span>
                  </div>

                  <div>
                    <span className="value-highlight">{formatCurrency(compra.valorTotal)}</span>
                  </div>

                  <div>
                    <button
                      className="download-btn"
                      onClick={(e) => handleDownloadXml(compra.chaveNfe, e)}
                      title="Baixar XML"
                    >
                      <FiDownload />
                    </button>
                  </div>
                </div>
              ))}
              {purchaseLoading && (
                <div className="history-loader">
                  <div className="spinner" />
                  Carregando...
                </div>
              )}
              {purchaseHasMore && !purchaseLoading && purchaseHistory.length > 0 && (
                <div ref={setPurchaseObserverTarget} style={{ height: '10px' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "reforma" && (
        <ReformaTab produto={produtoData} theme={theme} formatCurrency={formatCurrency} formatNumber={formatNumber} />
      )}

    </div>
  </Container>
  );
}
